import { PLACE_FIELDS } from './config'

export type CrmType = {
  address: {
    street1: string
    street2: string
    zip: string
    city: string
    state: string // for state/province
  }
  accountName: string
  accountStatus: string
  businessUnit: string
  editorSwisscard: string
  leadIdGiver: string
  leadInitiator: string
  leadInitiatorEmail: string
  leadInitiatorPhone: string
  leadStatus: string
  mainPhone: string | undefined
  majorIndustry: string
  merchantType: string
  notes: string | undefined
  reason: string
  sourceCampaign: string
  sourceChannel: string
  website: string | undefined
}

export type GeocoderCrmPropsType = {
  address: {
    street1: string
    street2: string
    zip: string
    city: string
    state: string // for state/province
  }
}

export type PlaceCrmPropsType = {
  accountName: string
  mainPhone: string | undefined
  notes: string | undefined
  website: string | undefined
}

export type PredictionType = {
  label: string
  value: string
}

export type NearbyResultType = {
  name: string
  placeId?: string
  vicinity?: string
}

export type ResultType = {
  name: string
  address: string
  placeId?: string
}

export const otherCrmProperties: CrmType = {
  accountName: '',
  accountStatus: '',
  address: {
    street1: '',
    street2: '',
    zip: '',
    city: '',
    state: '',
  },
  businessUnit: '',
  editorSwisscard: '',
  leadIdGiver: '',
  leadInitiator: '',
  leadInitiatorEmail: '',
  leadInitiatorPhone: '',
  leadStatus: '',
  mainPhone: '',
  majorIndustry: '',
  merchantType: '',
  notes: '',
  reason: '',
  sourceCampaign: '',
  sourceChannel: '',
  website: '',
}

export const attemptToStartAtNavigatorGeoLocation = (map: google.maps.Map, zoom: number) => {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      const position = {
        lat: coords.latitude,
        lng: coords.longitude,
      }
      map.setCenter(position)
      map.setZoom(zoom)
    })
  }
}

// Lookup method that extracts values from address components (an array of GeocoderAddressComponent items)
// by supplying an array of types for finding a match.
const addressComponentFinder =
  (addressComponents: google.maps.GeocoderAddressComponent[] | undefined) => (types: string[]) => {
    return addressComponents?.find((addressComponent: google.maps.GeocoderAddressComponent) =>
      addressComponent.types.some((type: string) => types.includes(type)),
    )?.long_name
  }

const getAddress = (geocoderResult: google.maps.GeocoderResult | google.maps.places.PlaceResult) => {
  const getAddressComponent = addressComponentFinder(geocoderResult?.address_components)
  return {
    street1: getAddressComponent(['route']) || '',
    street2: getAddressComponent(['street_number']) || '',
    zip: getAddressComponent(['postal_code']) || '',
    city: getAddressComponent(['locality', 'political', 'administrative_area_level_2']) || '',
    state: getAddressComponent(['administrative_area_level_1']) || '',
  }
}

export const buildCrmResult = (
  map: google.maps.Map,
  geocoderResult: google.maps.GeocoderResult,
  placeId: string,
): Promise<CrmType> => {
  return new Promise((resolve, reject) => {
    const placesService = new google.maps.places.PlacesService(map)
    placesService.getDetails(
      { placeId: placeId, fields: PLACE_FIELDS },
      (placeResult: google.maps.places.PlaceResult, status) => {
        if (!placeResult || status !== google.maps.places.PlacesServiceStatus.OK) {
          reject(undefined)
        } else {
          const geocoderProps = mapGeocoderResultToCrm(geocoderResult)
          const placeProps = mapPlaceResultToCrm(placeResult)
          const crmResult = {
            ...otherCrmProperties,
            ...geocoderProps,
            ...placeProps,
          }
          resolve(crmResult)
        }
      },
    )
  })
}

export const mapPredictionsToAutocompleteResults = (
  queryAutocompletePredictions: google.maps.places.QueryAutocompletePrediction[] | null,
): PredictionType[] => {
  return (
    queryAutocompletePredictions?.map((queryAutocompletePrediction: google.maps.places.QueryAutocompletePrediction) => {
      return {
        label: queryAutocompletePrediction.description,
        value: queryAutocompletePrediction.place_id,
      }
    }) || []
  )
}

const mapGeocoderResultToCrm = (geocoderResult: google.maps.GeocoderResult): GeocoderCrmPropsType => {
  return { address: getAddress(geocoderResult) }
}

export const mapPlaceResultToCrm = (
  placeResult: google.maps.places.PlaceResult,
): GeocoderCrmPropsType & PlaceCrmPropsType => {
  return {
    accountName: placeResult?.name,
    address: getAddress(placeResult),
    mainPhone: placeResult?.formatted_phone_number || placeResult?.international_phone_number || '',
    notes: placeResult?.price_level?.toString() || '',
    website: placeResult?.website || '',
  }
}

export const computeNameAndOrAddress = (name: string, address: string): string => {
  if (!name && !address) {
    return ''
  }

  return name && address.includes(name) ? address : [name, address].filter(Boolean).join(', ')
}

export const logError = (namespace: string, status: string, statuses: string[]) => {
  if (statuses.includes(status)) {
    console.error(`${namespace} error: ${status}`)
  }
}
